import React, { useEffect } from 'react';
import { Stack, IconButton, InputLabel, Box, Tooltip } from '@mui/material';
import { inchesToFeetAndInches } from '../utils/measurementUtils';
import { toastError } from '../../../../../Toaster/Toast';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { NumberField } from '@base-ui-components/react/number-field';
import { TbCaretDownFilled, TbCaretUpFilled } from 'react-icons/tb';
import { Lock, LockOpen, InfoOutlined } from '@mui/icons-material';

interface Props {
	labelInches: string;
	valueInches: number;
	onChange: (inches: number) => void;
	locked?: boolean;
	readOnlyInfo?: string;
	maxFeet?: number;
	reinitialize?: number;
	toastLabel?: string;
	minFeet?: number;
}

const FeetInchesInput: React.FC<Props> = ({
	labelInches,
	valueInches,
	onChange,
	locked,
	readOnlyInfo,
	maxFeet,
	reinitialize,
	toastLabel,
	minFeet,
}) => {
	const theme = useTheme();
	const { feet: initialFeet, inches: initialInches } =
		inchesToFeetAndInches(valueInches);

	const [feet, setFeet] = React.useState(initialFeet.toString());
	const [inches, setInches] = React.useState(initialInches.toString());
	const [disabled, setDisabled] = React.useState(locked);
	const isEffectivelyDisabled = !!readOnlyInfo || disabled;
	const [showFeetButtons, setShowFeetButtons] = React.useState(false);
	const [showInchesButtons, setShowInchesButtons] = React.useState(false);
	const onChangeRef = React.useRef(onChange);
	onChangeRef.current = onChange;
	const isInitialRender = React.useRef(true);

	useEffect(() => {
		const { feet: newFeet, inches: newInches } =
			inchesToFeetAndInches(valueInches);
		setFeet(newFeet.toString());
		setInches(newInches.toString());
	}, [valueInches, reinitialize]);

	const feetRef = React.useRef(feet);
	const inchesRef = React.useRef(inches);
	feetRef.current = feet;
	inchesRef.current = inches;

	useEffect(() => {
		if (isInitialRender.current) {
			isInitialRender.current = false;
			return;
		}

		const handler = setTimeout(() => {
			let totalInches = 0;

			if (feet !== '') {
				totalInches += parseInt(feet) * 12;
			}

			if (inches !== '') {
				totalInches += parseInt(inches);
			}

			if (maxFeet && totalInches > maxFeet * 12) {
				const { feet: clampedFeet, inches: clampedInches } =
					inchesToFeetAndInches(maxFeet * 12);
				setFeet(clampedFeet.toString());
				setInches(clampedInches.toString());
				toastError({
					title: 'Error',
					description: `${toastLabel || labelInches} can't be more than ${maxFeet} feet`,
				});
				if (onChangeRef.current) {
					onChangeRef.current(maxFeet * 12);
				}
			} else if (minFeet && totalInches < minFeet * 12) {
				const { feet: campedFeet, inches: clampedInches } =
					inchesToFeetAndInches(minFeet * 12);
				setFeet(campedFeet.toString());
				setInches(clampedInches.toString());
				toastError({
					title: 'Error',
					description: `${toastLabel || labelInches} can't be less than ${minFeet} feet`,
				});
				if (onChangeRef.current) {
					onChangeRef.current(minFeet * 12);
				}
			} else {
				if (onChangeRef.current) {
					onChangeRef.current(totalInches);
				}
			}
		}, 200);

		return () => {
			clearTimeout(handler);
		};
	}, [feet, inches]);

	const calculateAndCallOnChange = (feetValue: string, inchesValue: string) => {
		let totalInches = 0;

		if (feetValue !== '') {
			totalInches += parseInt(feetValue) * 12;
		}

		if (inchesValue !== '') {
			totalInches += parseInt(inchesValue);
		}

		if (maxFeet && totalInches > maxFeet * 12) {
			setFeet(maxFeet.toString());
			setInches('0');
			toastError({
				title: 'Error',
				description: `${toastLabel || labelInches} can't be more than ${maxFeet} feet`,
			});
			return;
		}
	};

	const handleInchesChange = (value: number | null) => {
		if (value === null || isNaN(value)) {
			setInches('');
			calculateAndCallOnChange(feet, '');
			return;
		}

		let inchesVal = value;
		let feetVal = parseInt(feet || '0', 10);

		if (inchesVal > 12) {
			feetVal += Math.floor(inchesVal / 12);
			inchesVal = inchesVal % 12;
		}
		if (inchesVal < 0) {
			const borrow = Math.ceil(Math.abs(inchesVal) / 12);
			if (feetVal >= borrow) {
				feetVal -= borrow;
				inchesVal = 12 * borrow + inchesVal;
			} else {
				feetVal = 0;
				inchesVal = 0;
			}
		}

		const newFeet = feetVal.toString();
		const newInches = inchesVal.toString();

		setFeet(newFeet);
		setInches(newInches);
		calculateAndCallOnChange(newFeet, newInches);
	};

	const handleFeetChange = (value: number | null) => {
		if (value === null || isNaN(value)) {
			setFeet('');
			calculateAndCallOnChange('', inches);
			return;
		}

		let feetVal = value;
		if (feetVal < 0) feetVal = 0;

		if (maxFeet && feetVal > maxFeet) {
			toastError({
				title: 'Error',
				description: `${toastLabel || labelInches} can't be more than ${maxFeet} feet`,
			});
			feetVal = maxFeet;
		}

		const newFeet = feetVal.toString();
		setFeet(newFeet);
		calculateAndCallOnChange(newFeet, inches);
	};

	return (
		<Stack>
			<InputLabel
				sx={{
					mb: 0.75,
					fontWeight: 500,
					fontSize: 14,
					display: 'flex',
					alignItems: 'center',
					gap: 0.5,
				}}
				htmlFor={`${labelInches.toLowerCase().replace(/\s+/g, '-')}-feet`}
			>
				{labelInches}
				{locked && (
					<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
						<Tooltip title={disabled ? 'Unlock input' : 'Lock input'}>
							<IconButton
								aria-label={disabled ? 'Unlock input' : 'Lock input'}
								onClick={() => setDisabled((prev) => !prev)}
								size="small"
								sx={{
									p: 0.5,
									color: 'inherit',
									fontSize: '1.1em',
									verticalAlign: 'middle',
								}}
								tabIndex={-1}
							>
								{disabled ? (
									<Lock sx={{ fontSize: '1.1em' }} />
								) : (
									<LockOpen sx={{ fontSize: '1.1em' }} />
								)}
							</IconButton>
						</Tooltip>
					</Box>
				)}
				{readOnlyInfo && (
					<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
						<Tooltip title={readOnlyInfo}>
							<InfoOutlined
								sx={{
									fontSize: '1.1em',
									color: 'text.secondary',
									cursor: 'help',
								}}
							/>
						</Tooltip>
					</Box>
				)}
			</InputLabel>
			<Stack
				direction="row"
				spacing={0.75}
				alignItems="center"
				sx={{
					width: '100%',
					'& > div': {
						display: 'flex',
						alignItems: 'center',
						flex: 1,
						'& [role="group"]': {
							display: 'flex',
							alignItems: 'center',
							gap: theme.spacing(0.5),
							background: theme.palette.background.paper,
							borderWidth: 1,
							borderStyle: 'solid',
							borderColor: theme.palette.secondary[300],
							borderRadius: theme.shape.borderRadius,
							padding: theme.spacing(0.5, 1),
							boxShadow: theme.shadows[1],
							width: '100%',
							'& input': {
								flex: 1,
								minWidth: 0,
								width: '100%',
								textAlign: 'center',
								fontSize: 14,
								outline: 'none',
								color: theme.palette.text.primary,
								fontWeight: 500,
								padding: theme.spacing(0.5),
							},
							'& input:disabled': {
								color: theme.palette.text.disabled,
								WebkitTextFillColor: theme.palette.text.disabled,
							},
						},
					},
				}}
			>
				<NumberField.Root
					id={`${labelInches.toLowerCase().replace(/\s+/g, '-')}-feet`}
					value={feet === '' ? null : Number(feet)}
					onValueChange={handleFeetChange}
					disabled={isEffectivelyDisabled}
					min={0}
					max={maxFeet}
				>
					<NumberField.Group
						style={{
							background: isEffectivelyDisabled
								? theme.palette.action.disabledBackground
								: 'inherit',
						}}
					>
						<NumberField.Input
							onFocus={() => setShowFeetButtons(true)}
							onBlur={() => setShowFeetButtons(false)}
							onChange={(e) => setFeet(e.target.value)}
						/>
						{!showFeetButtons ? (
							<Typography
								sx={{
									color: theme.palette.text.secondary,
									fontWeight: 500,
									height: '100%',
									width: theme.spacing(2),
									display: 'flex',
									alignItems: 'center',
								}}
							>
								ft
							</Typography>
						) : (
							<Stack direction="column">
								<NumberField.Increment>
									<TbCaretUpFilled alignmentBaseline="after-edge" />
								</NumberField.Increment>
								<NumberField.Decrement>
									<TbCaretDownFilled alignmentBaseline="after-edge" />
								</NumberField.Decrement>
							</Stack>
						)}
					</NumberField.Group>
				</NumberField.Root>

				<NumberField.Root
					id={`${labelInches.toLowerCase().replace(/\s+/g, '-')}-inches`}
					value={inches === '' ? null : Number(inches)}
					onValueChange={handleInchesChange}
					disabled={isEffectivelyDisabled}
					min={0}
				>
					<NumberField.Group
						style={{
							background: isEffectivelyDisabled
								? theme.palette.action.disabledBackground
								: 'inherit',
						}}
					>
						<NumberField.Input
							onFocus={() => setShowInchesButtons(true)}
							onBlur={() => setShowInchesButtons(false)}
							onChange={(e) => setInches(e.target.value)}
						/>
						{!showInchesButtons ? (
							<Typography
								sx={{
									color: theme.palette.text.secondary,
									fontWeight: 500,
									height: '100%',
									width: theme.spacing(2),
									display: 'flex',
									alignItems: 'center',
								}}
							>
								in
							</Typography>
						) : (
							<Stack direction="column">
								<NumberField.Increment>
									<TbCaretUpFilled alignmentBaseline="after-edge" />
								</NumberField.Increment>
								<NumberField.Decrement>
									<TbCaretDownFilled alignmentBaseline="after-edge" />
								</NumberField.Decrement>
							</Stack>
						)}
					</NumberField.Group>
				</NumberField.Root>
			</Stack>
		</Stack>
	);
};

export default FeetInchesInput;
